import { z } from 'zod/v4';

/**
 * Translation keys of the subscription form's publish checks — the issue messages
 * of {@link formLegacyDataRulesSchema}.
 */
export const FORM_WIDGET_RULE_MESSAGES = [
    'address_book_required',
    'pipeline_required',
    'deal_name_required',
    'hidden_control_value_required',
    'variable_required',
] as const;

export type FormWidgetRuleMessage = (typeof FORM_WIDGET_RULE_MESSAGES)[number];

/** One failed check: the data path it belongs to and its translation key. */
export type FormWidgetRuleIssue = {
    path: (string | number)[];
    message: FormWidgetRuleMessage;
};

/** The part of the form data the checks read. */
export type FormWidgetRulesData = {
    createAddressBook?: boolean | null;
    selectedAddressBook?: unknown;
    createContactAndDeal?: boolean | null;
    selectedPipeline?: unknown;
    dealName?: string | null;
    elements?: ReadonlyArray<{
        type: string;
        value?: unknown;
        crmContactVariable?: unknown;
        crmDealVariable?: unknown;
    }> | null;
};

const isEmpty = (value: unknown): boolean => value === null || value === undefined || value === '';

/**
 * What a subscription form still lacks before it can be published. Not part of
 * the data schema: an unfinished form is a valid, saveable state.
 */
export function getFormWidgetRuleIssues(data: FormWidgetRulesData): FormWidgetRuleIssue[] {
    const issues: FormWidgetRuleIssue[] = [];

    if (data.createAddressBook && isEmpty(data.selectedAddressBook)) {
        issues.push({ path: ['selectedAddressBook'], message: 'address_book_required' });
    }

    if (data.createContactAndDeal && isEmpty(data.selectedPipeline)) {
        issues.push({ path: ['selectedPipeline'], message: 'pipeline_required' });
    }

    if (data.createContactAndDeal && isEmpty(data.dealName)) {
        issues.push({ path: ['dealName'], message: 'deal_name_required' });
    }

    (data.elements ?? []).forEach((element, index) => {
        if (element.type === 'email' || element.type === 'phone') {
            return;
        }

        // On the element, not its `value`: old elements may lack the key, and a signal form has no field
        // for a missing key to hang the error on.
        if (element.type === 'hidden' && isEmpty(element.value)) {
            issues.push({ path: ['elements', index], message: 'hidden_control_value_required' });
        }

        // Textareas were exempt from the "one CRM variable at least" check from the start.
        if (element.type !== 'textarea' && isEmpty(element.crmContactVariable) && isEmpty(element.crmDealVariable)) {
            issues.push({ path: ['elements', index], message: 'variable_required' });
        }
    });

    return issues;
}

/**
 * {@link getFormWidgetRuleIssues} as a schema over the form `data`, for
 * `validateStandardSchema` — each issue lands on the field it names.
 */
export const formLegacyDataRulesSchema = z
    .custom<FormWidgetRulesData>((value) => typeof value === 'object' && value !== null)
    .superRefine((data, ctx) => {
        getFormWidgetRuleIssues(data).forEach(({ path, message }) => ctx.addIssue({ code: 'custom', path, message }));
    });
